import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Truck, User, MapPin, Phone, Mail, Lock, ArrowRight, CheckCircle } from 'lucide-react';
import './Checkout.css';

function Checkout({ cartItems, clearCart, customer }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    address: customer?.address || '',
    city: customer?.city || '',
    country: customer?.country || 'Saudi Arabia',
    postalCode: customer?.postalCode || '',
    paymentMethod: 'card',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 500 ? 0 : 50;
  const tax = subtotal * 0.15;
  const total = subtotal + shipping + tax;
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email address';
    if (!form.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!form.address.trim()) newErrors.address = 'Address is required';
    if (!form.city.trim()) newErrors.city = 'City is required';
    if (!form.country.trim()) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setTimeout(() => {
      const orderId = Math.floor(1000 + Math.random() * 9000);
      clearCart();
      navigate('/order-success', {
        state: {
          orderId,
          total,
          customerName: form.fullName,
          email: form.email,
        },
      });
    }, 1500);
  };

  if (cartItems.length === 0) {
    return (
      <div className="checkout-empty">
        <h2>Your cart is empty</h2>
        <p>Add items before proceeding to checkout</p>
        <Link to="/collection" className="btn btn-gold">Shop Collection</Link>
      </div>
    );
  }

  return (
    <div className="checkout">
      <section className="checkout-header">
        <h1>Secure Checkout</h1>
        <div className="checkout-steps">
          <span className="step done">Cart</span>
          <span className="step-line"></span>
          <span className="step active">Checkout</span>
          <span className="step-line"></span>
          <span className="step">Confirmation</span>
        </div>
      </section>

      <div className="checkout-layout">
        <form className="checkout-form" onSubmit={handleSubmit}>
          {!customer && (
            <div className="checkout-login-banner">
              <User size={18} />
              <span>Have an account?</span>
              <Link to="/login">Sign in for faster checkout</Link>
            </div>
          )}

          {customer && (
            <div className="checkout-logged-in">
              <CheckCircle size={18} />
              <span>Logged in as <strong>{customer.name}</strong></span>
            </div>
          )}

          <div className="form-section">
            <h3><User size={18} /> Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Your full name" />
                {errors.fullName && <span className="form-error">{errors.fullName}</span>}
              </div>
            </div>
            <div className="form-row two-col">
              <div className="form-group">
                <label><Mail size={14} /> Email *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label><Phone size={14} /> Phone *</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="+966 50 123 4567" />
                {errors.phone && <span className="form-error">{errors.phone}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3><MapPin size={18} /> Shipping Address</h3>
            <div className="form-group">
              <label>Street Address *</label>
              <input name="address" value={form.address} onChange={handleChange} placeholder="Street address, building, apartment" />
              {errors.address && <span className="form-error">{errors.address}</span>}
            </div>
            <div className="form-row three-col">
              <div className="form-group">
                <label>City *</label>
                <input name="city" value={form.city} onChange={handleChange} placeholder="City" />
                {errors.city && <span className="form-error">{errors.city}</span>}
              </div>
              <div className="form-group">
                <label>Country *</label>
                <select name="country" value={form.country} onChange={handleChange}>
                  <option>Saudi Arabia</option>
                  <option>UAE</option>
                  <option>Qatar</option>
                  <option>Kuwait</option>
                  <option>Bahrain</option>
                  <option>Oman</option>
                  <option>UK</option>
                  <option>USA</option>
                  <option>Canada</option>
                  <option>Pakistan</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Postal Code</label>
                <input name="postalCode" value={form.postalCode} onChange={handleChange} placeholder="12345" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3><CreditCard size={18} /> Payment Method</h3>
            <div className="payment-options">
              {[
                { value: 'card', label: 'Credit / Debit Card', icon: '💳' },
                { value: 'apple', label: 'Apple Pay', icon: '🍎' },
                { value: 'cod', label: 'Cash on Delivery', icon: '💰' },
              ].map(opt => (
                <label key={opt.value} className={`payment-option ${form.paymentMethod === opt.value ? 'selected' : ''}`}>
                  <input type="radio" name="paymentMethod" value={opt.value} checked={form.paymentMethod === opt.value} onChange={handleChange} />
                  <span className="pay-icon">{opt.icon}</span>
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Order Notes (optional)</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Special instructions for delivery..." />
          </div>

          <button type="submit" className="btn btn-gold btn-place-order" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="loading-spinner">Processing...</span>
            ) : (
              <>Place Order — {total.toFixed(2)} SAR <ArrowRight size={18} /></>
            )}
          </button>

          <div className="checkout-secure">
            <Lock size={14} />
            <span>Your information is encrypted and secure</span>
          </div>
        </form>

        <aside className="checkout-summary">
          <h3>Order Summary</h3>
          <div className="checkout-items">
            {cartItems.map(item => (
              <div key={item.id} className="checkout-item">
                <img src={item.image} alt={item.name} />
                <div className="checkout-item-info">
                  <span className="item-name">{item.name}</span>
                  <span className="item-qty">Qty: {item.quantity}</span>
                </div>
                <span className="item-price">{(item.price * item.quantity).toFixed(0)} SAR</span>
              </div>
            ))}
          </div>

          <div className="summary-rows">
            <div className="summary-row">
              <span>Subtotal ({itemCount} items)</span>
              <span>{subtotal.toFixed(0)} SAR</span>
            </div>
            <div className="summary-row">
              <span><Truck size={14} /> Shipping</span>
              <span className={shipping === 0 ? 'free' : ''}>{shipping === 0 ? 'FREE' : `${shipping} SAR`}</span>
            </div>
            <div className="summary-row">
              <span>Tax (15%)</span>
              <span>{tax.toFixed(2)} SAR</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{total.toFixed(2)} SAR</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Checkout;
